import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/utils/lazyRetry';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Глобальный ErrorBoundary.
 *
 * Зачем нужен: приложение использует React.lazy + code-splitting (см. vite.config.ts
 * `manualChunks`) и PWA service-worker. При client-side навигации на lazy-маршрут
 * браузер может не загрузить чанк (типичный случай — после нового деплоя старый
 * index.html ссылается на имена чанков, которых уже нет, либо их закэшировал SW).
 * Без ErrorBoundary такая ошибка размонтирует всё дерево React → белый экран.
 * После жёсткой перезагрузки (F5) грузятся свежие чанки → страница снова видна,
 * что в точности совпадает с симптомом «белый экран, после перезагрузки всё ок».
 *
 * Поэтому: chunk-load-ошибки чиним автоматически перезагрузкой страницы (корневая
 * причина — устаревший кэш чанков, и перезагрузка решает её лучше любого фолбэка),
 * а остальные рантайм-ошибки показываем пользователю с понятной кнопкой перезагрузки.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Chunk-load failure — корневая причина в устаревшем кэше/ссылках на чанки.
    // Надёжнее всего перезагрузить страницу: браузер подтянет свежий index.html
    // с актуальными хэшами чанков. Гвардим флагом, чтобы не зациклиться.
    if (isChunkLoadError(error) && !window.sessionStorage.getItem('aq:chunk-reloaded')) {
      window.sessionStorage.setItem('aq:chunk-reloaded', '1');
      window.location.reload();
      return;
    }

    console.error('Uncaught error caught by ErrorBoundary:', error, errorInfo);
  }

  handleReload = (): void => {
    // Сбрасываем флаг авто-перезагрузки, чтобы новый заход снова мог сработать.
    window.sessionStorage.removeItem('aq:chunk-reloaded');
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
            <svg
              className="h-6 w-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
            Что-то пошло не так
          </h1>
          <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
            Не удалось загрузить страницу. Обычно это связано с обновлением приложения
            — перезагрузите страницу, и всё должно заработать.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Перезагрузить страницу
          </button>
        </div>
      </div>
    );
  }
}
